
import { defineComponent, onBeforeMount, reactive } from 'vue'

// Components
import PageTitle from '@/common/components/UI/PageTitle/index.vue'
import TheButton from '@/common/components/UI/TheButton/index.vue'
import FailedFetch from '@/common/components/UI/FailedFetch/index.vue'
import TheTable from '@/common/components/UI/TheTable/index.vue'
import TheSelect from '@/common/components/UI/TheSelect/index.vue'
import Cheap from '@/common/components/UI/Cheap/index.vue'
import TheInput from '@/common/components/UI/TheInput/index.vue'
import TableScrollWrapper from '@/common/components/UI/TableScrollWrapper/index.vue'

// Constants
import { CouriersCarsColumns } from '@/core/constants/columns/CouriersCars.columns'

// Classes
import { DataState } from '@/core/classes/DataState.class'

// Realisation
import { couriers } from '@/core/realisations/couriers.realisation'
import { FTL } from '@/core/realisations/FTL.realisation'
import { carriers } from '@/core/realisations/carriers.realisation'

// Types
import { CarStatus, CouirerCarT } from '@/core/types/couriers.types'
import { FTLCarTypeT } from '@/core/types/ftl.types'
import { CarrierT } from '@/core/types/carriers.types'

// Composable
import useAccount from '@/common/composable/useAccount'
import useNotifications from '@/common/composable/useNotifications'

export default defineComponent({
    components: {
        PageTitle,
        TheButton,
        FailedFetch,
        TheTable,
        TheSelect,
        Cheap,
        TheInput,
        TableScrollWrapper,
    },
    setup() {
        const { havePermissions } = useAccount()
        const notifications = useNotifications()

        const state = reactive({
            cars: new DataState<CouirerCarT>({}),
        })

        const filters_state = reactive({
            limit: 50,
            //
            companyId: null as number | null,
            ftlCarTypeId: null as number | null,
            statusId: null as number | null,
            number: null as string | null,
        })

        const data_state = reactive({
            carriers: new DataState<CarrierT>({}),
            ftl_car_types: new DataState<FTLCarTypeT>({}),
            statuses: new DataState<CarStatus>({}),
        })

        //

        const loadCarriers = async (page: number, search?: string) => {
            data_state.carriers.pagination.current_page = page

            try {
                const params = {
                    page,
                    search,
                    getAllCarriers: 1,
                    limit: 50,
                    statusId: 2,
                }

                data_state.carriers.is_loading = true
                const data = await carriers.getList(params)

                data_state.carriers.list = data.data
                data_state.carriers.pagination.total_pages = data.meta.last_page

                data_state.carriers.is_failed = false
                data_state.carriers.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.carriers.failure.catchError(error)

                if (!data_state.carriers.failure.error_state.is_cancel) {
                    data_state.carriers.is_failed = true
                }
            } finally {
                if (!data_state.carriers.failure.error_state.is_cancel) {
                    data_state.carriers.is_loading = false
                }
            }
        }

        const loadMoreCarriers = async (page: number) => {
            if (page > data_state.carriers.pagination.total_pages) return
            data_state.carriers.pagination.current_page = page

            try {
                const params = {
                    page,
                    getAllCarriers: 1,
                    limit: 50,
                    statusId: 2,
                }

                data_state.carriers.is_loading = true

                const response = await carriers.getList(params)
                data_state.carriers.list = data_state.carriers.list.concat(response.data)

                data_state.carriers.pagination.total_pages = response.meta.last_page

                data_state.carriers.is_failed = false
                data_state.carriers.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.carriers.failure.catchError(error)

                if (!data_state.carriers.failure.error_state.is_cancel) {
                    data_state.carriers.is_failed = true
                }
            } finally {
                if (!data_state.carriers.failure.error_state.is_cancel) {
                    data_state.carriers.is_loading = false
                }
            }
        }

        const searchCarriers = (search_query: string) => {
            loadCarriers(1, search_query)
        }

        const unshiftFindedCarrier = async (carrier: CarrierT) => {
            await loadCarriers(data_state.carriers.pagination.current_page)
            data_state.carriers.list.unshift(carrier)
        }

        //

        const loadFtlCarTypes = async () => {
            data_state.ftl_car_types.pagination.current_page = 1

            try {
                data_state.ftl_car_types.is_loading = true
                const response = await FTL.getCarTypes()

                data_state.ftl_car_types.list = response.data
                data_state.ftl_car_types.pagination.total_pages = 1

                data_state.ftl_car_types.is_failed = false
                data_state.ftl_car_types.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.ftl_car_types.failure.catchError(error)

                if (!data_state.ftl_car_types.failure.error_state.is_cancel) {
                    data_state.ftl_car_types.is_failed = true
                }
            } finally {
                if (!data_state.ftl_car_types.failure.error_state.is_cancel) {
                    data_state.ftl_car_types.is_loading = false
                }
            }
        }

        const loadCarStatuses = async (page: number) => {
            data_state.statuses.pagination.current_page = 1

            try {
                data_state.statuses.is_loading = true
                const response = await couriers.getCarsStatuses({ page })

                data_state.statuses.list = response.data
                data_state.statuses.pagination.total_pages = 1

                data_state.statuses.is_failed = false
                data_state.statuses.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.statuses.failure.catchError(error)

                if (!data_state.statuses.failure.error_state.is_cancel) {
                    data_state.statuses.is_failed = true
                }
            } finally {
                if (!data_state.statuses.failure.error_state.is_cancel) {
                    data_state.statuses.is_loading = false
                }
            }
        }

        const loadCars = async (page = 1) => {
            state.cars.pagination.current_page = page

            try {
                state.cars.is_loading = true

                const response = await couriers.getCars({
                    page,
                    companyId: filters_state.companyId,
                    ftlCarTypeId: filters_state.ftlCarTypeId,
                    statusId: filters_state.statusId,
                    number: filters_state.number,
                    limit: filters_state.limit,
                })

                state.cars.list = response.data
                state.cars.pagination.total_pages = response.meta.last_page
                state.cars.pagination.total_items = response.meta.total

                state.cars.is_failed = false
                state.cars.failure.error_state.is_cancel = false
            } catch (error) {
                state.cars.failure.catchError(error)

                if (!state.cars.failure.error_state.is_cancel) {
                    state.cars.is_failed = true
                }
            } finally {
                if (!state.cars.failure.error_state.is_cancel) {
                    state.cars.is_loading = false
                }
            }
        }

        const onLimitChange = (limit: number) => {
            filters_state.limit = limit
            loadCars(state.cars.pagination.current_page)
        }

        onBeforeMount(() => {
            loadCars()
            loadCarriers(1)
            loadFtlCarTypes()
            loadCarStatuses(1)
        })

        const cutName = (name: string, max_length: number): string => {
            if (!name) return ''

            if (name.length > max_length) {
                let name_parts = name.split('')
                name_parts = name_parts.splice(0, max_length - 1)
                name_parts.push('...')
                return name_parts.join('')
            }

            return name
        }

        const delete_state = reactive({
            is_loading: false,
            deleting_id: null as number | null,
        })

        const deleteCar = async (car_id: number) => {
            const answer = confirm('Вы действительно хотите удалить автомобиль?')
            if (!answer) return

            try {
                delete_state.deleting_id = car_id
                delete_state.is_loading = true

                await couriers.deleteCar(car_id)
                notifications.notify({ title: 'Автомобиль удален', type: 'success' })

                loadCars(state.cars.pagination.current_page)
            } catch (error) {
                console.error(error)
                notifications.notifyError({
                    error,
                    title: 'При удалении автомобиля произошла ошибка',
                })
            } finally {
                delete_state.is_loading = false
                delete_state.deleting_id = null
            }
        }

        const searchCars = () => {
            loadCars(1)
        }

        const resetFilters = () => {
            filters_state.companyId = null
            filters_state.ftlCarTypeId = null
            filters_state.statusId = null
            filters_state.number = null

            loadCars(1)
        }

        return {
            CouriersCarsColumns,
            state,
            filters_state,
            data_state,
            delete_state,
            deleteCar,
            loadCars,
            havePermissions,
            cutName,
            //
            loadCarriers,
            loadMoreCarriers,
            searchCarriers,
            unshiftFindedCarrier,
            //
            loadFtlCarTypes,
            loadCarStatuses,
            searchCars,
            resetFilters,
            onLimitChange,
        }
    },
})
